import React, { forwardRef, Ref, useRef, useState, useMemo, ReactNode, ReactElement } from 'react';
import { useMergeRefs } from '@floating-ui/react';
import { AriaFocusProps } from '../../types/common';
import { DropdownProps } from './Dropdown';
import { DropdownArrowButton } from './DropdownArrowButton';
import { filterItemStartsWith, getKeyFromItem } from '../../utils/shared';
import { Input } from '../input';
import { Stack } from '../stack';
import { getZIndex } from '../../styles';
import { IconProps } from '../icons/generated';
import { DropdownOptions } from './DropdownOptions';
import { useDropdownFloating, useDropdownInteractions } from './hooks';
import { DropdownInputOption } from './types';

export interface DropdownInputProps<OptionValue>
  extends Pick<
    DropdownProps<OptionValue>,
    | 'width'
    | 'zIndex'
    | 'disabled'
    | 'options'
    | 'placeholder'
    | 'status'
    | 'value'
    | 'size'
    | 'optionItemHeight'
    | 'autoPlacement'
    | 'enableVirtualScroll'
  > {
  /**
   * options에 없는 값을 허용할 지 여부입니다.
   */
  allowCustomValue?: boolean;
  startElement?: ReactElement<IconProps> | ReactNode;
  endElement?: ReactElement<IconProps> | ReactNode;
  /**
   * 입력된 검색결과를 전달합니다.
   */
  onSearch?: (inputValue: string) => void;
  onChange?: (value: OptionValue | undefined) => void;
  onKeyDown?: (event: React.KeyboardEvent<Element>) => void;
  options: Array<DropdownInputOption<OptionValue>>;
  keyExtractor?: (item: DropdownInputOption<OptionValue>, index: number) => string | number;
  optionFilter?: (inputValue: string, item: DropdownInputOption<OptionValue>, index: number) => boolean;
}

interface DropdownOpenState {
  // 사용자가 값을 1글자라도 입력했는데 값이 존재하거나, 또는 화살표를 눌렀다면 자동 완성이
  // 보여지게 됩니다.
  // 화살표를 눌러서 버튼을 열었기 때문에, 모든 항목을 노출해야 하는지 여부입니다.
  openByArrow: boolean;
  // 자동 완성이 열려있는 상태에서 사용자가 화살표를 눌러 강제로 닫았는지 여부입니다.
  forceClosed: boolean;
  // 사용자가 직접 입력한 값입니다. 초기 값 상태와 동일한 경우 null입니다.
  inputValue: string | null;
}

function DropdownInputInner<OptionValue extends string | number>(
  {
    width,
    zIndex = getZIndex('dropdown'),
    disabled,
    options,
    placeholder,
    status,
    value,
    size: sizeProp = 'medium',
    optionItemHeight,
    unstableFocusVisible,
    allowCustomValue,
    keyExtractor = getKeyFromItem,
    optionFilter = filterItemStartsWith,
    autoPlacement: autoPlacementProps = true,
    startElement,
    endElement,
    onSearch,
    onChange,
    onKeyDown,
    enableVirtualScroll = false,
  }: DropdownInputProps<OptionValue> & AriaFocusProps,
  ref: Ref<HTMLInputElement>,
) {
  const optionListRef = useRef<Array<HTMLElement | null>>([]);
  const inputRef = useRef<HTMLInputElement | null>(null);
  const mergedInputRef = useMergeRefs([inputRef, ref]);

  const [focusIndex, setFocusIndex] = useState<number | null>(null);
  const [openState, setOpenState] = useState<DropdownOpenState>({
    openByArrow: false,
    forceClosed: false,
    inputValue: null,
  });
  const { openByArrow, forceClosed: closedByArrow, inputValue } = openState;
  const selectedOption = useMemo(() => options.find((option) => option.value === value), [value, options]);
  const displayedValue = (allowCustomValue ? value : inputValue != null ? inputValue : selectedOption?.label) ?? '';

  const filteredOptions = useMemo(() => {
    if (openByArrow || inputValue == null) {
      return options;
    }
    return options.filter((option, index) => optionFilter(inputValue, option, index));
  }, [openByArrow, inputValue, optionFilter, options]);
  const open = !closedByArrow && (inputValue != null || openByArrow);
  const handleClose = () => {
    setOpenState({ openByArrow: false, forceClosed: false, inputValue: null });
  };

  const { refs, floatingStyles, context, isPositioned } = useDropdownFloating({
    open,
    onOpenChange: handleClose,
    autoPlacement: autoPlacementProps,
    sizeMiddlewareOptions: {
      apply({ rects, elements }) {
        Object.assign(elements.floating.style, {
          width: `${rects.reference.width}px`,
        });
      },
    },
  });

  const { getReferenceProps, getFloatingProps, getItemProps } = useDropdownInteractions({
    context,
    disabledInteractions: ['useTypeahead', 'useClick'],
    useListNavigationProps: {
      listRef: optionListRef,
      activeIndex: focusIndex,
      onNavigate: setFocusIndex,
      virtual: true,
      loop: true,
    },
  });

  const handleChangeInput = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value;
    setOpenState({ openByArrow: false, forceClosed: false, inputValue: value });
    setFocusIndex(0);
    onSearch?.(value);
    onChange?.((allowCustomValue ? value : undefined) as OptionValue);
  };

  const handleClickArrow = () => {
    if (!disabled) {
      setOpenState((v) => ({ ...v, openByArrow: true, forceClosed: open }));
      inputRef.current?.focus();
    }
  };

  return (
    <>
      <Stack direction='row' ref={refs.setReference} width={width}>
        <Input
          {...getReferenceProps({
            'ref': mergedInputRef,
            'aria-autocomplete': 'list',
            'autoComplete': 'off',
            'onChange': handleChangeInput,
            'value': displayedValue,
            'disabled': disabled,
            'placeholder': placeholder,
            'onKeyDown'(event) {
              onKeyDown?.(event);
              if (
                event.key === 'Enter' &&
                !event.nativeEvent.isComposing &&
                focusIndex != null &&
                filteredOptions[focusIndex] &&
                (!allowCustomValue || filteredOptions[focusIndex].value)
              ) {
                setOpenState({ inputValue: null, openByArrow: false, forceClosed: false });
                onChange?.(filteredOptions[focusIndex].value);
                setFocusIndex(null);
              }
              if (event.key === 'Tab') {
                setOpenState((v) => ({ ...v, forceClosed: true }));
              }
              if ((event.key === 'ArrowUp' && !focusIndex) || event.key === 'End') {
                setFocusIndex(filteredOptions.length - 1);
              }
              if ((event.key === 'ArrowDown' && focusIndex === filteredOptions.length - 1) || event.key === 'Home') {
                setFocusIndex(0);
              }
            },
            'onFocus'() {
              setOpenState(({ inputValue }) => ({
                openByArrow: true,
                forceClosed: open,
                inputValue,
              }));
              inputRef.current?.select();
            },
          })}
          status={status}
          size={sizeProp}
          startElement={startElement}
          endElement={
            endElement ? (
              endElement
            ) : endElement === null || endElement === false ? null : (
              <DropdownArrowButton
                onClick={handleClickArrow}
                expanded={open}
                disabled={disabled}
                selected={inputValue !== null}
              />
            )
          }
        />
      </Stack>
      <DropdownOptions
        initialFocus={-1}
        refs={refs}
        size={sizeProp}
        optionItemHeight={optionItemHeight}
        setRefs={(node, index) => {
          optionListRef.current[index] = node;
        }}
        floatingStyles={floatingStyles}
        getFloatingProps={getFloatingProps}
        getItemProps={getItemProps}
        onClose={() => {
          handleClose();
          setFocusIndex(null);
        }}
        noDataText={'일치하는 항목이 없습니다.'}
        value={value}
        zIndex={zIndex}
        unstableFocusVisible={unstableFocusVisible}
        // @ts-expect-error: TS2322
        keyExtractor={keyExtractor}
        onChange={onChange}
        context={context}
        focusIndex={focusIndex}
        options={filteredOptions}
        isPositioned={isPositioned}
        enableVirtualScroll={enableVirtualScroll}
      />
    </>
  );
}

export const DropdownInput = forwardRef(DropdownInputInner) as (<OptionValue extends string | number>(
  props: DropdownInputProps<OptionValue> & AriaFocusProps & { ref?: Ref<HTMLInputElement> },
) => JSX.Element) & { displayName?: string };

DropdownInput.displayName = 'DropdownInput';
